import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useConfirmationDialogue } from '../../providers/ConfirmationDialogue.provider';
import { usePendingData } from '../../providers/PendingData.provider';

import { useBillingPackages } from '../../providers/BillingPackages.provider';
import type { CustomPackageResponseValue } from '../useApi/usePostCustomPackage';

const usePostCustomPackageDialogue = ({
  success,
  errorMessage,
  isApproved,
}: CustomPackageResponseValue): { destination?: string; navigate: () => void; showDialogue: () => void } => {
  const history = useHistory();
  const [destination, setDestination] = useState<string>();

  const { t } = useTranslation('translations', { keyPrefix: 'Hooks.UsePostCustomPackageDialogue' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { refreshBillingPackages } = useBillingPackages();
  const { provideProps, showDialogue } = useConfirmationDialogue();
  const { reset } = usePendingData();

  const navigate = () => {
    if (destination) history.push(destination);
  };

  useEffect(() => {
    const handlers = {
      success: () =>
        provideProps({
          title: t('successTitle', { defaultValue: 'Success! Your custom billing package has been created.' }),
          status: 'success',
          subTitle: isApproved
            ? t('successSubTitleApproved', {
                defaultValue: 'Your billing package has been approved and is ready for use.',
              })
            : t('successSubTitlePending', { defaultValue: 'Your billing package is pending approval' }),
          confirm: {
            label: t('backToBillingPackagesButton', { defaultValue: 'Back to Billing Packages' }),
            action: () => {
              reset();
              refreshBillingPackages();
              setDestination('/billing-packages');
            },
          },
          cancel: {
            label: tGlobal('Buttons.stayHere'),
            action: () => {
              reset();
              refreshBillingPackages();
            },
          },
        }),
      errorMessage: () => {
        provideProps({
          title: t('errorTitle', { defaultValue: 'Error! Your custom billing package has not been created.' }),
          subTitle: errorMessage,
          status: 'error',
          confirm: {
            label: t('backToBillingPackagesButton', { defaultValue: 'Back to Billing Packages' }),
            action: () => {
              reset();
              setDestination('/billing-packages');
            },
          },
          cancel: {
            label: tGlobal('Buttons.stayHereAndTryAgain'),
            action: () => {},
          },
        });
      },
    };

    if (success) return handlers.success();

    return handlers.errorMessage();
  }, [success, errorMessage, isApproved]);

  return { destination, navigate, showDialogue };
};

export default usePostCustomPackageDialogue;
